import React from 'react';
import cn from 'classnames';
import { Avatar, Button, Tag } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import moment from 'moment';
// Helpers
import {
  currentYear,
  enumToLabel,
  ExternalTalentTableRow,
  getLocationFilterKeys,
  getLocationFilterValues,
  getExternalTalentSocialUrlValues,
  getSportFilterValue,
  hasLocationFilters,
  LocationFilterPrefix,
  LocationFilterValues,
} from 'helpers/externalTalents';
// Types
import { Sports_sports } from 'api/sports/types/Sports';
import {
  ExtTalentAthleticLevel,
  ExtTalentSocialPlatform,
  StoreGender,
} from 'api/graphql-global-types';
import { AdminSearchExtTalents_adminSearchExtTalents_entities } from 'api/ExtTalent/types/adminSearchExtTalents';
// Constants
import { commonTimeFormat } from 'constants/global';
// UI
import { Column, TableFilter } from 'ui/Table';
// Components
import LocationFilterDropdown from './components/LocationFilterDropdown/LocationFilterDropdown';
import SportFilterDropdown from './components/SportFilterDropdown/SportFilterDropdown';
// Styles
import styles from './ExternalTalents.module.scss';

const booleanFilters = [
  { text: 'Yes', value: 'true' },
  { text: 'No', value: 'false' },
];

type ExternalTalentColumnsOptions = {
  loadingSports: boolean;
  onApplyLocationFilters: (
    prefix: LocationFilterPrefix,
    values: LocationFilterValues,
    confirm: () => void
  ) => void;
  onApplySportFilter: (sport: string, confirm: () => void) => void;
  onEditTalent: (
    talent: AdminSearchExtTalents_adminSearchExtTalents_entities
  ) => void;
  onResetLocationFilters: (
    prefix: LocationFilterPrefix,
    clearFilters?: () => void
  ) => void;
  onResetSportFilter: (clearFilters?: () => void) => void;
  searchFilters: TableFilter[];
  sports: Sports_sports[];
};

const renderTextValue = (value: string): string => value || '-';

const renderBooleanTag = (value: 'false' | 'true'): JSX.Element =>
  value === 'true' ? (
    <Tag color="green">Yes</Tag>
  ) : (
    <Tag color="default">No</Tag>
  );

const renderAvatar = (
  talent: AdminSearchExtTalents_adminSearchExtTalents_entities
): JSX.Element => (
  <Avatar shape="square" size={48} src={talent.avatarUrl || undefined}>
    {(talent.firstName || talent.lastName || '?').charAt(0)}
  </Avatar>
);

const renderActionButton = (
  talent: AdminSearchExtTalents_adminSearchExtTalents_entities,
  onEditTalent: (
    talent: AdminSearchExtTalents_adminSearchExtTalents_entities
  ) => void
): JSX.Element => (
  <Button onClick={() => onEditTalent(talent)} type="primary">
    Edit
  </Button>
);

const renderSocialUrls = (
  _value: string,
  record: ExternalTalentTableRow
): JSX.Element | string => {
  const socialUrls = getExternalTalentSocialUrlValues(record.original.socials);

  if (!socialUrls.length) {
    return '-';
  }

  return (
    <>
      {socialUrls.map((url, index) => (
        <React.Fragment key={`${url}-${index}`}>
          <a href={url} rel="noopener noreferrer" target="_blank">
            {url}
          </a>
          {index < socialUrls.length - 1 ? ' ' : null}
        </React.Fragment>
      ))}
    </>
  );
};

const getLocationColumnFilterProps = ({
  onApplyLocationFilters,
  onResetLocationFilters,
  prefix,
  searchFilters,
}: {
  onApplyLocationFilters: (
    prefix: LocationFilterPrefix,
    values: LocationFilterValues,
    confirm: () => void
  ) => void;
  onResetLocationFilters: (
    prefix: LocationFilterPrefix,
    clearFilters?: () => void
  ) => void;
  prefix: LocationFilterPrefix;
  searchFilters: TableFilter[];
}) => ({
  filteredValue: hasLocationFilters(searchFilters, prefix)
    ? getLocationFilterKeys(searchFilters, prefix)
    : null,
  filterDropdown: ({
    clearFilters,
    confirm,
    selectedKeys,
    setSelectedKeys,
  }: {
    clearFilters?: () => void;
    confirm: () => void;
    selectedKeys: string[];
    setSelectedKeys: (keys: string[]) => void;
  }) => {
    const currentValues = getLocationFilterValues(searchFilters, prefix);
    const values = {
      city: selectedKeys?.[2] ?? currentValues.city,
      country: selectedKeys?.[0] ?? currentValues.country,
      state: selectedKeys?.[1] ?? currentValues.state,
    };

    return (
      <LocationFilterDropdown
        onApply={() => onApplyLocationFilters(prefix, values, confirm)}
        onCityChange={(event) =>
          setSelectedKeys([values.country, values.state, event.target.value])
        }
        onCountryChange={(event) =>
          setSelectedKeys([event.target.value, values.state, values.city])
        }
        onReset={() => onResetLocationFilters(prefix, clearFilters)}
        onStateChange={(event) =>
          setSelectedKeys([values.country, event.target.value, values.city])
        }
        values={values}
      />
    );
  },
  filterIcon: (filtered: boolean) => (
    <FilterOutlined
      className={cn({
        [styles.filterIconActive]: filtered,
      })}
    />
  ),
});

const getSportColumnFilterProps = ({
  loadingSports,
  onApplySportFilter,
  onResetSportFilter,
  searchFilters,
  sports,
}: {
  loadingSports: boolean;
  onApplySportFilter: (sport: string, confirm: () => void) => void;
  onResetSportFilter: (clearFilters?: () => void) => void;
  searchFilters: TableFilter[];
  sports: Sports_sports[];
}) => ({
  filteredValue: getSportFilterValue(searchFilters)
    ? [getSportFilterValue(searchFilters)]
    : null,
  filterDropdown: ({
    clearFilters,
    confirm,
    selectedKeys,
    setSelectedKeys,
  }: {
    clearFilters?: () => void;
    confirm: () => void;
    selectedKeys: string[];
    setSelectedKeys: (keys: string[]) => void;
  }) => {
    const currentSport =
      selectedKeys?.[0] ?? getSportFilterValue(searchFilters);

    return (
      <SportFilterDropdown
        currentSport={currentSport || ''}
        loadingSports={loadingSports}
        onApply={() => onApplySportFilter(currentSport || '', confirm)}
        onChange={(value) => setSelectedKeys(value ? [value] : [])}
        onReset={() => onResetSportFilter(clearFilters)}
        sports={sports}
      />
    );
  },
  filterIcon: (filtered: boolean) => (
    <FilterOutlined
      className={cn({
        [styles.filterIconActive]: filtered,
      })}
    />
  ),
});

export const getExternalTalentColumns = ({
  loadingSports,
  onApplyLocationFilters,
  onApplySportFilter,
  onEditTalent,
  onResetLocationFilters,
  onResetSportFilter,
  searchFilters,
  sports,
}: ExternalTalentColumnsOptions): Column<ExternalTalentTableRow, string>[] => [
  {
    title: 'Avatar',
    dataIndex: 'original',
    width: 80,
    render: renderAvatar,
  },
  {
    title: 'External Talent ID',
    dataIndex: 'id',
    key: 'id',
    width: 220,
    render: renderTextValue,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 180,
    withSearch: true,
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'contactEmail',
    width: 220,
    withSearch: true,
    render: renderTextValue,
  },
  {
    title: 'Sport',
    dataIndex: 'sport',
    key: 'sports',
    width: 150,
    render: renderTextValue,
    ...getSportColumnFilterProps({
      loadingSports,
      onApplySportFilter,
      onResetSportFilter,
      searchFilters,
      sports,
    }),
  },
  {
    title: 'Team',
    dataIndex: 'team',
    key: 'team',
    width: 150,
    withSearch: true,
    render: renderTextValue,
  },
  {
    title: 'League',
    dataIndex: 'league',
    key: 'league',
    width: 150,
    withSearch: true,
    render: renderTextValue,
  },
  {
    title: 'College',
    dataIndex: 'collegeUniversity',
    key: 'collegeUniversity',
    width: 180,
    withSearch: true,
    render: renderTextValue,
  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
    width: 220,
    render: renderTextValue,
    ...getLocationColumnFilterProps({
      onApplyLocationFilters,
      onResetLocationFilters,
      prefix: 'location',
      searchFilters,
    }),
  },
  {
    title: 'Hometown',
    dataIndex: 'hometown',
    key: 'hometown',
    width: 220,
    render: renderTextValue,
    ...getLocationColumnFilterProps({
      onApplyLocationFilters,
      onResetLocationFilters,
      prefix: 'hometown',
      searchFilters,
    }),
  },
  {
    title: 'Athletic Level',
    dataIndex: 'athleticLevels',
    key: 'athleticLevels',
    filters: Object.values(ExtTalentAthleticLevel).map((value) => ({
      text: enumToLabel(value),
      value,
    })),
    width: 180,
    withCheckboxFilters: true,
    render: (value: ExtTalentAthleticLevel | null) => enumToLabel(value),
  },
  {
    title: 'Gender',
    dataIndex: 'genders',
    key: 'genders',
    filters: Object.values(StoreGender).map((value) => ({
      text: enumToLabel(value),
      value,
    })),
    width: 140,
    withCheckboxFilters: true,
    render: (value: StoreGender | null) => enumToLabel(value),
  },
  {
    title: 'Profile Names',
    dataIndex: 'profileNames',
    key: 'profileName',
    width: 240,
    withSearch: true,
    render: renderTextValue,
  },
  {
    title: 'Social Urls',
    dataIndex: 'socialUrls',
    forceSearchRender: true,
    key: 'socialUrl',
    width: 240,
    withSearch: true,
    render: renderSocialUrls,
  },
  {
    title: 'Platforms',
    dataIndex: 'socialPlatforms',
    key: 'socialPlatform',
    filters: Object.values(ExtTalentSocialPlatform).map((value) => ({
      text: enumToLabel(value),
      value,
    })),
    width: 180,
    withRadioFilters: true,
    render: renderTextValue,
  },
  {
    title: 'Followers',
    dataIndex: 'followersCount',
    key: 'followersCount',
    defaultNumericalValues: [0, 100000000],
    width: 140,
    withNumericalRangeFilter: true,
    render: (followersCount: number) =>
      followersCount ? followersCount.toLocaleString() : '0',
  },
  {
    title: 'Bio Hashtags',
    dataIndex: 'bioHashtags',
    key: 'bioHashtags',
    width: 260,
    withSearch: true,
    render: renderTextValue,
  },
  {
    title: 'Avg Engagement',
    dataIndex: 'avgEngagement',
    key: 'avgEngagement',
    defaultNumericalValues: [0, 5],
    width: 160,
    withFloatRangeFilter: true,
    render: (avgEngagement: number | null) =>
      typeof avgEngagement === 'number' ? avgEngagement.toFixed(2) : '-',
  },
  {
    title: 'Birth Year',
    dataIndex: 'birthYear',
    key: 'birthYear',
    defaultNumericalValues: [1900, currentYear],
    width: 140,
    withNumericalRangeFilter: true,
    render: (birthYear: number | null) => birthYear || '-',
  },
  {
    title: 'Has Email',
    dataIndex: 'hasEmail',
    key: 'hasEmail',
    filters: booleanFilters,
    width: 130,
    withRadioFilters: true,
    render: renderBooleanTag,
  },
  {
    title: 'On Platform',
    dataIndex: 'isOnPlatform',
    key: 'isOnPlatform',
    filters: booleanFilters,
    width: 130,
    withRadioFilters: true,
    render: renderBooleanTag,
  },
  {
    title: 'Updated',
    dataIndex: 'updatedAt',
    width: 170,
    render: (updatedAt: string) =>
      updatedAt ? moment(updatedAt).format(commonTimeFormat) : '-',
  },
  {
    title: 'Action',
    dataIndex: 'original',
    fixed: 'right' as const,
    width: 100,
    render: (talent) => renderActionButton(talent, onEditTalent),
  },
];
